import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import useQueryParams from '../../hooks/useQueryParams'
import { LoadingSection, showToast } from '@quickmd-llc/design-system'
import { errorCodes, getUserFriendlyErrorMessage } from '../../utils/errormessages'
import EditStateSettingsView from './EditStateSettingsView'
import { getStateByAbbreviation } from '../../utils/common'
import { DiscardChangesPopup } from '../../components/discard-changes-popup/DiscardChangesPopup'
import { useRegisterNavigationGuard } from '../../components/authenticated-layout/navigationGuard/useNavigationGuard'
import { useEditStateSettingsCallbacks } from './hooks/useEditStateSettingsCallbacks'
import { useEditStateSettingsEffects } from './hooks/useEditStateSettingsEffects'
import { useStateSettings } from '../management/StatesSettings/hooks/useStateSettings'
import type { ApplicationSettingsPerStateAdminApiContract } from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'

const EditStateSettings = () => {
  const location = useLocation()
  const getQueryParam = useQueryParams(location)
  const stateRef = useRef((getQueryParam('state') ?? '').toUpperCase())
  const stateName = getStateByAbbreviation(stateRef.current)

  const { settingsPerState } = useStateSettings()

  const [stateSettings] = useState<ApplicationSettingsPerStateAdminApiContract>(
    settingsPerState.find((s) => s.StateCode === stateRef.current) ?? {},
  )
  const [requiresDrugScreening, setRequiresDrugScreening] = useState<boolean | null | undefined>(
    stateSettings.DrugScreenHtml ? true : false,
  )
  const [isPhoneVisitAllowed, setIsPhoneVisitAllowed] = useState<boolean | undefined>(
    stateSettings.IsPhoneVisitCommunicationTypeAllowed,
  )
  const [drugScreeningText, setDrugScreeningText] = useState<string | null | undefined>(
    // Keep the stored HTML intact so the rich-text editor renders its formatting (previously
    // stripHtml flattened it to plain text for the old textarea).
    stateSettings.DrugScreenHtml ?? '',
  )
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [isConfirmationPopUpOpened, setIsConfirmationPopUpOpened] = useState<boolean | 'back'>(false)
  const [newActiveTab, setNewActiveTab] = useState<string | { redirect: string }>('')
  const [drugError, setDrugError] = useState('')
  // Destination requested via a nav-bar click while there are unsaved changes; consumed on discard.
  const pendingNavigationRef = useRef<string | null>(null)

  const {
    save,
    cancel,
    changeRequiresDrugScreening,
    changePhoneRule,
    setDrugScreenRules,
    confirmationPopUpDiscardBtnClick,
    confirmationPopUpCloseBtnClick,
  } = useEditStateSettingsCallbacks({
    stateSettings,
    setError,
    setLoading,
    isPhoneVisitAllowed,
    drugScreeningText,
    requiresDrugScreening,
    isConfirmationPopUpOpened,
    setDrugScreeningText,
    setDrugError,
    setRequiresDrugScreening,
    setIsPhoneVisitAllowed,
    setIsConfirmationPopUpOpened,
    setNewActiveTab,
    newActiveTab,
    location,
    pendingNavigationRef,
  })

  const { hasChanged } = useEditStateSettingsEffects({
    error,
    stateSettings,
    requiresDrugScreening,
    setError,
    setIsConfirmationPopUpOpened,
    isPhoneVisitAllowed,
    drugScreeningText,
    drugError,
    setDrugError,
    setIsPhoneVisitAllowed,
    setDrugScreeningText,
    setRequiresDrugScreening,
  })

  useEffect(() => {
    if (error) {
      showToast({ type: 'error', message: error, position: 'bottom-right' })
    }
  }, [error])

  // Intercept nav-bar navigations while there are unsaved changes: stash the destination and open
  // the discard-changes popup instead of leaving the page.
  useRegisterNavigationGuard((destination) => {
    if (!hasChanged) return false
    pendingNavigationRef.current = destination
    setIsConfirmationPopUpOpened(true)
    return true
  })

  // Cancel routes through the same popup when there are unsaved changes. pendingNavigationRef stays
  // null so discard falls back to the callback's own cancel() (back to the states list).
  const handleCancel = () => {
    if (hasChanged) {
      pendingNavigationRef.current = null
      setIsConfirmationPopUpOpened(true)
    } else {
      cancel()
    }
  }

  const rawLocationState: unknown = location.state
  const locationSearch =
    (
      rawLocationState !== null &&
      typeof rawLocationState === 'object' &&
      'search' in rawLocationState &&
      typeof rawLocationState.search === 'string'
    ) ?
      rawLocationState.search
    : undefined

  const breadCrumbs = useMemo(
    () => [
      {
        site: 'Management',
        location: `/management${locationSearch ?? '?activeTab=States'}`,
      },
      {
        site: stateName ?? '',
        location: '',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stateName is derived from a ref (stateRef) and is stable for the component lifetime so it is intentionally omitted; hasChanged is intentionally included to rebuild the breadcrumb array whenever the form's dirty state changes
    [locationSearch, hasChanged],
  )

  if (!stateName) {
    setTimeout(() => {
      showToast({
        type: 'error',
        message: getUserFriendlyErrorMessage(errorCodes.NOT_VALID_VALUE, null, 'State'),
        position: 'bottom-right',
      })
      cancel()
    }, 3500)
    return null
  }

  return (
    <>
      <DiscardChangesPopup
        isOpen={!!isConfirmationPopUpOpened}
        entityName={getStateByAbbreviation(stateSettings.StateCode ?? '') ?? stateName}
        onDiscard={confirmationPopUpDiscardBtnClick}
        onKeepEditing={confirmationPopUpCloseBtnClick}
      />
      {loading ?
        <LoadingSection />
      : null}
      <EditStateSettingsView
        breadCrumbs={breadCrumbs}
        stateName={stateName}
        onSave={save}
        onCancel={handleCancel}
        onChangePhoneRule={changePhoneRule}
        onChangeRequiresDrugScreening={changeRequiresDrugScreening}
        isPhoneVisitAllowed={isPhoneVisitAllowed}
        requiresDrugScreening={requiresDrugScreening}
        onSetDrugScreenRules={setDrugScreenRules}
        drugScreeningText={drugScreeningText}
        drugError={drugError}
      />
    </>
  )
}

export default EditStateSettings
